import { useState, useEffect, useCallback, useRef } from "react";
import { IndoorPositioningStations } from "../../../../api";
import IndoorPositioningLive from "../../../../api/IndoorPositioningLive";

// How long a person's last position may be reused while polls carry none. The
// backend sends a position only while it is <= 30 s old and treats a person as
// active for 120 s, so 90 s keeps a 60 s-interval tag steady yet clears a dot
// within ~120 s of its last real fix.
const CACHED_POSITION_TTL_MS = 90_000;

// State holds METRES only. Every pixel position on the canvas is computed at
// render time from the current viewport (LiveCanvas), so a viewport change
// (resize today, zoom/pan later) re-renders without touching state, without
// blanking the canvas and without restarting these polls.
export const useLiveData = (
  machineID,
  open = true,
  isPaused,
  floorPlanLoading = false,
) => {
  const [personnel, setPersonnel] = useState([]);
  const [baseStations, setBaseStations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    totalPersonnel: 0,
    activePersonnel: 0,
    warningPersonnel: 0,
    sosPersonnel: 0,
    offlinePersonnel: 0,
    onDutyPersonnel: 0,
    onDutyOnline: 0,
    onDutyOffline: 0,
    onDutyWarning: 0,
    onDutySos: 0,
    activeAlerts: 0,
  });
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [stationsLoading, setStationsLoading] = useState(false);

  const isActiveRef = useRef(true);
  const lastKnownPositionsRef = useRef(new Map());

  useEffect(() => {
    if (!open || !machineID) return;

    const loadStations = async () => {
      setStationsLoading(true);
      try {
        const response = await IndoorPositioningStations.GetStations(machineID);
        if (response.code === 200 && Array.isArray(response.data)) {
          const placed = response.data
            .filter(
              (s) =>
                s.canvas_x !== null &&
                s.canvas_x !== undefined &&
                s.canvas_y !== null &&
                s.canvas_y !== undefined,
            )
            .map((s) => ({
              ...s,
              position_meters: { x: s.canvas_x, y: s.canvas_y },
            }));
          setBaseStations(placed);
        } else {
          setBaseStations([]);
        }
      } catch (error) {
        console.error("載入基站資料失敗:", error);
        setBaseStations([]);
      } finally {
        setStationsLoading(false);
      }
    };

    setConnectionStatus("connected");
    loadStations();

    return () => {
      setConnectionStatus("disconnected");
      isActiveRef.current = false;
    };
  }, [open, machineID]);

  useEffect(() => {
    if (!open || !machineID || isPaused || floorPlanLoading) return;

    isActiveRef.current = true;

    // Clear previous machine's data immediately when machineID changes
    setPersonnel([]);
    setAlerts([]);
    lastKnownPositionsRef.current = new Map();

    const fetchLiveData = async () => {
      if (!isActiveRef.current) return;

      try {
        const personnelResponse =
          await IndoorPositioningLive.GetPersonnel(machineID);

        if (personnelResponse.code === 200 && isActiveRef.current) {
          const personnelData = (personnelResponse.data.personnel || []).map(
            (person) => {
              const now = Date.now();
              let cached = lastKnownPositionsRef.current.get(person.id);
              if (cached && now - cached.receivedAt > CACHED_POSITION_TTL_MS) {
                lastKnownPositionsRef.current.delete(person.id);
                cached = undefined;
              }
              const effectivePositionMeters =
                person.position_meters || cached?.position_meters || null;
              const effectiveLastPositionUpdate =
                person.lastPositionUpdate || cached?.lastPositionUpdate || null;

              if (person.position_meters) {
                lastKnownPositionsRef.current.set(person.id, {
                  position_meters: person.position_meters,
                  lastPositionUpdate: person.lastPositionUpdate,
                  positionMethod: person.positionMethod ?? null,
                  offSite: !!person.offSite,
                  positionAccuracy: person.positionAccuracy ?? null,
                  receivedAt: now,
                });
              }

              // The flags describe the position they were stored with, so a
              // dot drawn from the cache must not take this poll's (empty) ones.
              const source = person.position_meters ? person : cached || person;

              return {
                ...person,
                position_meters: effectivePositionMeters,
                lastPositionUpdate: effectiveLastPositionUpdate,
                positionMethod: source.positionMethod ?? null,
                offSite: !!source.offSite,
                positionAccuracy: source.positionAccuracy ?? null,
              };
            },
          );

          setPersonnel(personnelData);

          if (personnelResponse.data.stats) {
            setStats(personnelResponse.data.stats);
          }
        }
      } catch (error) {
        console.error("Failed to fetch personnel data:", error);
        if (isActiveRef.current) {
          setConnectionStatus("error");
        }
      }
    };

    const fetchAlerts = async () => {
      if (!isActiveRef.current) return;

      try {
        const alertResponse = await IndoorPositioningLive.GetAlerts(machineID);

        if (alertResponse.code === 200 && isActiveRef.current) {
          const newAlerts = alertResponse.data.alerts || [];
          // Replace alerts completely (backend now returns fixed historical alerts)
          setAlerts(newAlerts);
        }
      } catch (error) {
        console.error("Failed to fetch alerts:", error);
      }
    };

    fetchLiveData();
    fetchAlerts();

    const dataInterval = setInterval(fetchLiveData, 2000);
    const alertInterval = setInterval(fetchAlerts, 5000);

    return () => {
      isActiveRef.current = false;
      clearInterval(dataInterval);
      clearInterval(alertInterval);
    };
  }, [open, machineID, isPaused, floorPlanLoading]);

  useEffect(() => {
    if (personnel.length > 0 || alerts.length > 0) {
      const onDutyPersonnel = personnel.filter(
        (p) => p.onDuty === 1 || p.onDuty === true,
      );
      const onDutyOffline = onDutyPersonnel.filter(
        (p) => p.status === "offline",
      ).length;
      const onDutyWarning = onDutyPersonnel.filter(
        (p) => p.status === "warning",
      ).length;
      const onDutySos = onDutyPersonnel.filter(
        (p) => p.status === "sos",
      ).length;

      const calculatedStats = {
        totalPersonnel: personnel.length,
        activePersonnel: personnel.filter((p) => p.status === "active").length,
        warningPersonnel: personnel.filter((p) => p.status === "warning")
          .length,
        sosPersonnel: personnel.filter((p) => p.status === "sos").length,
        offlinePersonnel: personnel.filter((p) => p.status === "offline")
          .length,
        onDutyPersonnel: onDutyPersonnel.length,
        onDutyOnline: onDutyPersonnel.length - onDutyOffline,
        onDutyOffline: onDutyOffline,
        onDutyWarning: onDutyWarning,
        onDutySos: onDutySos,
        activeAlerts: alerts.filter((a) => !a.acknowledged).length,
      };
      setStats(calculatedStats);
    }
  }, [personnel, alerts]);

  const acknowledgeAlert = useCallback(
    async (alertId) => {
      try {
        const response = await IndoorPositioningLive.AcknowledgeAlert(
          machineID,
          alertId,
        );
        if (response.code === 200) {
          setAlerts((prev) =>
            prev.map((alert) =>
              alert.id === alertId ? { ...alert, acknowledged: true } : alert,
            ),
          );
        }
      } catch (error) {
        console.error("Failed to acknowledge alert:", error);
      }
    },
    [machineID],
  );

  const dismissAlert = useCallback(
    async (alertId) => {
      try {
        const response = await IndoorPositioningLive.DismissAlert(
          machineID,
          alertId,
        );
        if (response.code === 200) {
          setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
        }
      } catch (error) {
        console.error("Failed to dismiss alert:", error);
      }
    },
    [machineID],
  );

  return {
    personnel,
    baseStations,
    alerts,
    stats,
    connectionStatus,
    stationsLoading,
    acknowledgeAlert,
    dismissAlert,
  };
};
